/*
 * ---------------------------------------------------------------------------------
 * Copyright:
 *      NewtonGreen Technologies Pty. Ltd.
 *      Level 4, 175 Scott St.
 *      Newcastle, NSW, 2300
 *      Australia
 * 
 *      E-mail: support@newtongreen.com
 *      Tel: (02) 4925 5288
 *      Fax: (02) 4925 3068
 * 
 *      All Rights Reserved.
 * ---------------------------------------------------------------------------------
 */

/*
 * --------------------------------------------------------------------------------
 * This file contains the hook to use pre-registered patient columns.
 * --------------------------------------------------------------------------------
 */

/*
 * ---------------------------------------------------------------------------------
 * Imports - External
 * ---------------------------------------------------------------------------------
 */

/*
 * Used to create a context.
 */
import * as React from 'react';

/*
 * Used to type patients and institutions 
 */
import {
    AuthenticatedUserContext,
    IInstitution
} from '@ngt/opms';

import { Chip, IconButton, styled, Tooltip, TooltipProps } from '@mui/material';

/*
 * Used to format datetimes
 */

import { GridColDef } from '@mui/x-data-grid';



/*
 * ---------------------------------------------------------------------------------
 * Imports - External
 * ---------------------------------------------------------------------------------
 */

import { faBell, faFileSignature, faPen, faRotate } from '@fortawesome/pro-duotone-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { ConsentTypeEnum, Patient, StatusTypeEnum } from '../api/dtos';
import ConsentDialogContext, { ConsentDialogState } from '../contexts/ConsentDialogContext';
import { AppSettings } from '../AppSettings';
import { faTimes } from '@fortawesome/pro-solid-svg-icons';

export const CONSENT_EVENT_DEFINITION_ID = 2;
/*
 * ---------------------------------------------------------------------------------
 * Functions
 * ---------------------------------------------------------------------------------
 */
const StyledChip = styled(Chip)(({theme}) => ({
    backgroundColor: '#987ba7',
    color: theme.palette.common.white,
}));

const StyledButton = styled(IconButton)(({theme}) => ({
    padding: theme.spacing(0),
    marginLeft: theme.spacing(2),
    color: theme.palette.mode === 'dark' ?
        `${AppSettings.palette.primaryDark} !important` :
        `${AppSettings.palette.primaryLight} !important`, 

    '&:hover': {
        color: theme.palette.mode === 'dark' ?
        `${theme.palette.primary.light} !important` :
        `${theme.palette.primary.dark} !important`, 
        background: 'none',
    },

    '&:last-of-type': {
        marginRight: theme.spacing(0),
    },

}));


const StyledTooltip = styled(({ className, ...props }: TooltipProps) => (
    <Tooltip {...props} classes={{ tooltip: className }} />
))(({ theme }) => ({
    
    backgroundColor: theme.palette.mode === 'dark' ?
        `rgba(0, 0, 0, 0.87)` :
        theme.palette.common.white,
    color: theme.palette.mode === 'dark' ?
        theme.palette.common.white :
        `rgba(0, 0, 0, 0.87)`,
    boxShadow: theme.shadows[1],
    fontSize: 11,
    
}));

const useConsentGridColDefs = (institutions: IInstitution[] | null | undefined, showSite: boolean, showInitials: boolean, canConsentAdminister: boolean): GridColDef<Patient>[] => {

    const { data: user } = useContext(AuthenticatedUserContext);
    const navigate = useNavigate();

    const { setPatientStudyNumber, setDialogState } = React.useContext(ConsentDialogContext);

    const onSignClick = React.useCallback((consentGuid?: string | null) => {
        navigate(`/trial-consent/investigator/econsent/${consentGuid}`)
    }, [history]);

    const onStartClick = React.useCallback((studyNumber: string | null) => {
        setPatientStudyNumber(studyNumber);
        setDialogState(ConsentDialogState.Start);
    }, [setPatientStudyNumber, setDialogState]);

    const onRestartClick = React.useCallback((studyNumber: string | null) => {
        setPatientStudyNumber(studyNumber);
        setDialogState(ConsentDialogState.Restart);
    }, [setPatientStudyNumber, setDialogState]);

    const onNotifyClick = React.useCallback((studyNumber: string | null) => {
        setPatientStudyNumber(studyNumber);
        setDialogState(ConsentDialogState.Notify);
    }, [setPatientStudyNumber, setDialogState]);

    const onRefuseClick = React.useCallback((studyNumber: string | null) => {
        setPatientStudyNumber(studyNumber);
        setDialogState(ConsentDialogState.Refuse);
    }, [setPatientStudyNumber, setDialogState]);

    const columns = React.useMemo(() => {
        const c: (GridColDef<Patient> | boolean)[] = [
            {
                field: 'studyNumber',
                headerName: 'Temp. No',
                width: 140
            },
            showInitials && {
                field: 'initials',
                headerName: 'Initials'
            },
            {
                field: 'institutionCode',
                headerName: 'Site Code',
                renderCell: rowData => (
                    <StyledChip
                        label={rowData.row.institutionCode}
                        size="small"
                    />
                ),
                width: 110
            },
            {
                field: 'institutionId',
                headerName: 'Institution',
                valueGetter: (_value, row) =>  institutions?.find(i => i.id === row.institutionId)?.name,
                flex: 1
            },
            {
                field: 'consentTypeId',
                headerName: 'Type',
                valueGetter: (_value, row) =>  {
                    const consentEvent = row.consentEvents.find(x => x.eventDefinitionId === CONSENT_EVENT_DEFINITION_ID);

                    let consentType = !consentEvent?.dateStarted ?
                        'Pending'
                        :
                        consentEvent.consentTypeId === ConsentTypeEnum.Standard ?  'Standard' :
                            consentEvent.consentTypeId === ConsentTypeEnum.Remote ? 'eConsent' : ''

                    return consentType;
                },
                width: 140
            },
            {
                field: 'patientStateId',
                headerName: 'Status',
                valueGetter: (_value, row) => {
                    const consentEvent = row.consentEvents.find(x => x.eventDefinitionId === CONSENT_EVENT_DEFINITION_ID);

                    let consentStatus = !consentEvent?.dateStarted ?
                        'Pending'
                        :
                        !consentEvent.statusId ?
                            'Signing'
                            :
                            consentEvent.statusId === StatusTypeEnum.PatientSigning ? 'Patient Signing' :
                                consentEvent.statusId === StatusTypeEnum.InvestigatorSigning ? 'Investigator Signing' :
                                    consentEvent.statusId === StatusTypeEnum.Completed ? 'Complete' : '';

                    return consentStatus
                },
                width: 200
            },
            {
                field: 'id',
                headerName: ' ',
                filterable: false,
                sortable: false,
                renderCell: rowData => {
                    const consentEvent = rowData.row.consentEvents.find(x => x.eventDefinitionId === CONSENT_EVENT_DEFINITION_ID);

                    const restartTooltipText = <>
                        <strong>Restart Consent</strong>
                    </>

                    return <>
                        {
                            
                            !!canConsentAdminister && consentEvent?.investigator === user?.userName && <StyledTooltip title="Sign Consent Forms">
                                <StyledButton
                                    size="medium"
                                    onClick={(e) => {onSignClick(rowData.row.consentEvents?.find(x => x.eventDefinitionId === CONSENT_EVENT_DEFINITION_ID)?.guid); e.stopPropagation();}}
                                >
                                    <FontAwesomeIcon icon={faPen} fixedWidth />
                                </StyledButton>
                            </StyledTooltip>
                        }

                        {
                            
                            !!canConsentAdminister && !consentEvent?.dateStarted && <StyledTooltip title="Start Consent">
                                <StyledButton
                                    size="medium"
                                    onClick={(e) => {onStartClick(rowData.row.studyNumber); e.stopPropagation();}}
                                >
                                    <FontAwesomeIcon icon={faFileSignature} fixedWidth />
                                </StyledButton>
                            </StyledTooltip>
                        }

                        {
                            !!canConsentAdminister && !!consentEvent?.dateStarted && <StyledTooltip title={restartTooltipText}>
                                <StyledButton
                                    size="medium"
                                    onClick={(e) => {onRestartClick(rowData.row.studyNumber); e.stopPropagation();}}
                                >
                                    <FontAwesomeIcon icon={faRotate} fixedWidth />
                                </StyledButton>
                            </StyledTooltip>
                        }

                        <StyledTooltip title="Send Notification">
                            <StyledButton
                                size="medium"
                                onClick={(e) => {onNotifyClick(rowData.row.studyNumber); e.stopPropagation();}}
                            >
                                <FontAwesomeIcon icon={faBell} fixedWidth />
                            </StyledButton>
                        </StyledTooltip>

                        {
                            !!canConsentAdminister && <StyledTooltip title="Refuse Consent">
                                <StyledButton
                                    size="medium"
                                    onClick={(e) => {onRefuseClick(rowData.row.studyNumber); e.stopPropagation();}}
                                >
                                    <FontAwesomeIcon icon={faTimes} fixedWidth />
                                </StyledButton>
                            </StyledTooltip>
                        }
                    </>
                },
                width: 200
            },
        ];

        return c.filter(column => !!column) as GridColDef<Patient>[];
    }, [showSite, institutions, canConsentAdminister, showInitials]);

    return columns;
};

/*
 * ---------------------------------------------------------------------------------
 * Default Export
 * ---------------------------------------------------------------------------------
 */

export default useConsentGridColDefs;